/** @jsxImportSource ./simboluri-jsx */
// COSTUL STÂLPULUI ÎNTREG („daca fac asta imi trebuie un
// view unde sa imi dea costul pe tot stalpul").
//
// Cardul apare numai când omul cere stiva întreagă în pagina elementului —
// altfel costul afișat rămâne al nivelului, ca înainte. Prețurile și rețeta
// sunt ACELEAȘI (un singur modul: costElement.ts); ce se schimbă e domeniul
// însumării și rândul separat al mustăților din fundație, care se plătesc la
// infrastructură.

import { useEffect, useState } from "react";
import { StalpArmat } from "./tipuri";
import { bani } from "./bani";
import { CostStiva, costStiva } from "./costElement";
import { nr } from "./elemente";
import { useT } from "../i18n";

export default function CostStivaCard({
  projectId,
  cloneId = null,
  stiva,
}: {
  projectId: string;
  cloneId?: string | null;
  stiva: StalpArmat;
}) {
  const t = useT();
  const [cost, setCost] = useState<CostStiva | null>(null);
  const [gata, setGata] = useState(false);

  useEffect(() => {
    let viu = true;
    setCost(null);
    setGata(false);
    costStiva(projectId, stiva.element, cloneId)
      .then((c) => {
        if (!viu) return;
        setCost(c);
        setGata(true);
      })
      .catch(() => viu && setGata(true));
    return () => {
      viu = false;
    };
  }, [projectId, cloneId, stiva]);

  if (!gata) {
    return (
      <div className="ob-card" style={{ padding: "11px 14px" }}>
        <span className="ob-lbl">{t("struct.stiva.titlu")}</span>
        <div className="sg-muted" style={{ fontSize: 12, paddingTop: 6 }}>
          {t("struct.stiva.se_aduna")}
        </div>
      </div>
    );
  }
  if (!cost) {
    return (
      <div className="ob-card" style={{ padding: "11px 14px" }}>
        <span className="ob-lbl">{t("struct.stiva.titlu")}</span>
        <div className="sg-muted" style={{ fontSize: 12, paddingTop: 6 }}>
          {t("struct.stiva.fara_deviz")}
        </div>
      </div>
    );
  }

  return (
    <div className="ob-card" style={{ overflow: "hidden" }}>
      <div style={{ padding: "11px 14px", display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <b>{t("struct.stiva.titlu_cu_element", { element: stiva.element })}</b>
        <span className="ob-chip ob-c-mut">{t("struct.inv.kg_otel", { n: nr(cost.kgOtel, 1) })}</span>
        <span className="ob-chip ob-c-mut">{t("struct.inv.m3_beton", { n: nr(cost.betonM3, 2) })}</span>
        <span style={{ flex: 1 }} />
        <b className="ob-disp" style={{ fontSize: 17 }}>{bani(cost.totalEur)}</b>
      </div>
      <table className="ob-sol-tabel">
        <thead>
          <tr>
            <th>{t("struct.top.nivel")}</th>
            <th style={{ textAlign: "right", width: 80 }}>{t("struct.stiva.col_beton")}</th>
            <th style={{ textAlign: "right", width: 80 }}>{t("struct.stiva.col_cofraj")}</th>
            <th style={{ textAlign: "right", width: 76 }}>{t("struct.stiva.col_otel")}</th>
            <th style={{ textAlign: "right", width: 80 }}>{t("struct.stiva.col_material")}</th>
            <th style={{ textAlign: "right", width: 84 }}>{t("struct.stiva.col_manopera")}</th>
            <th style={{ textAlign: "right", width: 84 }}>{t("struct.stiva.col_total")}</th>
          </tr>
        </thead>
        <tbody>
          {cost.randuri.map((r) => (
            <tr key={r.ref} style={r.infra ? { background: "var(--app)" } : undefined}>
              <td style={{ fontSize: 12 }}>
                {r.ref}{" "}
                <span style={{ color: "var(--mut)" }}>{r.nivel}</span>
                {r.infra && (
                  <span className="ob-chip ob-c-mut" style={{ fontSize: 10, marginLeft: 6 }}
                        title={t("struct.stiva.infra_titlu")}>
                    {t("struct.stiva.infra")}
                  </span>
                )}
              </td>
              <td className="sg-mono" style={{ textAlign: "right", fontSize: 12 }}>
                {r.betonM3 != null ? `${nr(r.betonM3, 2)} m³` : "—"}
              </td>
              <td className="sg-mono" style={{ textAlign: "right", fontSize: 12 }}>
                {r.cofrajM2 != null ? `${nr(r.cofrajM2, 1)} m²` : "—"}
              </td>
              <td className="sg-mono" style={{ textAlign: "right", fontSize: 12 }}>
                {nr(r.kgOtel, 1)} kg
              </td>
              <td className="sg-mono" style={{ textAlign: "right", fontSize: 12 }}>
                {bani(r.materialEur)}
              </td>
              <td className="sg-mono" style={{ textAlign: "right", fontSize: 12 }}>
                {bani(r.manoperaEur)}
              </td>
              <td className="sg-mono" style={{ textAlign: "right", fontSize: 12, fontWeight: 600 }}>
                {bani(r.totalEur)}
              </td>
            </tr>
          ))}
          <tr style={{ background: "var(--accbg)" }}>
            <td style={{ fontWeight: 600 }}>{t("struct.stiva.tot_stalpul", { element: stiva.element })}</td>
            <td className="sg-mono" style={{ textAlign: "right", fontWeight: 600 }}>
              {nr(cost.betonM3, 2)} m³
            </td>
            <td className="sg-mono" style={{ textAlign: "right", fontWeight: 600 }}>
              {nr(cost.cofrajM2, 1)} m²
            </td>
            <td className="sg-mono" style={{ textAlign: "right", fontWeight: 600 }}>
              {nr(cost.kgOtel, 1)} kg
            </td>
            <td className="sg-mono" style={{ textAlign: "right", fontWeight: 600 }}>
              {bani(cost.materialEur)}
            </td>
            <td className="sg-mono" style={{ textAlign: "right", fontWeight: 600 }}>
              {bani(cost.manoperaEur)}
            </td>
            <td className="sg-mono" style={{ textAlign: "right", fontWeight: 700 }}>
              {bani(cost.totalEur)}
            </td>
          </tr>
        </tbody>
      </table>
      <div style={{ padding: "10px 14px", borderTop: "1px solid var(--line)", fontSize: 12, color: "var(--mut)", lineHeight: 1.5 }}>
        {t("struct.stiva.nota", { sursa: cost.sursa.join(" · ") })}
        {cost.betonM3 > 0 &&
          t("struct.stiva.densitate", { n: nr(cost.kgOtel / cost.betonM3) })}
        {cost.kgNeatribuit > 0.5 && (
          <>
            {" "}
            <b style={{ color: "var(--amb)" }}>
              {t("struct.stiva.neatribuit", { n: nr(cost.kgNeatribuit, 1) })}
            </b>{" "}
            {t("struct.stiva.neatribuit_dupa")}
          </>
        )}
      </div>
    </div>
  );
}
